
'use client';

import * as React from 'react';
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signOut,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  AuthError,
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { AppUser } from '@/lib/types';
import { doc, setDoc, updateDoc } from 'firebase/firestore';

const OWNER_EMAILS = ['artanidanial@gmail.com', 'artanidanial@outlook.com'];
const LIFETIME_PREMIUM_USERS = ['infosec.legend@gmail.com', 'asmaa.aus@gmail.com'];
const EARLY_SUPPORTER_CUTOFF_DATE = new Date('2025-01-01T00:00:00Z');


export interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  error: string | null;
  isOwner: boolean;
  isPremium: boolean;
  isEarlySupporter: boolean;
  signup: (name: string, email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updateUserProfile: (name: string, photoURL: string | null) => Promise<void>;
  checkPremiumStatus: () => void;
  resendVerificationEmail: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


const buildAppUser = (firebaseUser: FirebaseUser | null): AppUser | null => {
    if (!firebaseUser) return null;

    const userIsOwner = OWNER_EMAILS.includes(firebaseUser.email || '');
    
    const creationTime = firebaseUser.metadata.creationTime ? new Date(firebaseUser.metadata.creationTime) : new Date();
    const userIsEarlySupporter = creationTime < EARLY_SUPPORTER_CUTOFF_DATE;

    const localPremium = typeof window !== 'undefined' ? localStorage.getItem('noor-navigator-premium') === 'true' : false;
    const isLifetimePremium = LIFETIME_PREMIUM_USERS.includes(firebaseUser.email || '');

    return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        isOwner: userIsOwner,
        isEarlySupporter: userIsEarlySupporter,
        isPremium: userIsOwner || localPremium || isLifetimePremium,
        emailVerified: firebaseUser.emailVerified,
    };
}


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isPremium, setIsPremium] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('noor-navigator-premium') === 'true';
    }
    return false;
  });

  const isOwner = user?.isOwner ?? false;
  const isEarlySupporter = user?.isEarlySupporter ?? false;

  const checkPremiumStatus = useCallback(() => {
    if(typeof window !== 'undefined') {
        localStorage.setItem('noor-navigator-premium', 'true');
        setIsPremium(true);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      setLoading(true);
      const appUser = buildAppUser(firebaseUser);
      setUser(appUser);
      setIsPremium(appUser?.isPremium ?? false);
      setError(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);
  
  const handleAuthError = (err: any) => {
    const authError = err as AuthError;
    let message = authError.message;
    
    switch (authError.code) {
        case 'auth/email-already-in-use':
            message = "This email is already registered. Please try logging in.";
            break;
        case 'auth/invalid-credential':
             message = "Invalid email or password. Please try again.";
             break;
        default:
            message = "An authentication error occurred. Please try again later.";
            break;
    }
    
    setError(message);
    setLoading(false);
    throw err;
  }

  const signup = async (name: string, email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });
      await sendEmailVerification(userCredential.user);
      
      // Force a reload of the user to get the latest metadata, including creationTime.
      await userCredential.user.reload();
      const freshUser = auth.currentUser;
      
      const appUser = buildAppUser(freshUser);
      if (appUser) {
        const userDocRef = doc(db, 'users', appUser.uid);
        // Create a user profile document
        await setDoc(userDocRef, {
            uid: appUser.uid,
            displayName: appUser.displayName,
            photoURL: appUser.photoURL,
            email: appUser.email,
            isOwner: appUser.isOwner,
            isEarlySupporter: appUser.isEarlySupporter,
            isPremium: appUser.isPremium,
        }, { merge: true });
      }

      // Manually set the user to trigger immediate UI update. The onAuthStateChanged listener might be too slow.
      setUser(appUser); 
      setIsPremium(appUser?.isPremium ?? false);

    } catch (err: any) {
      handleAuthError(err);
    } finally {
        setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const appUser = buildAppUser(userCredential.user);
      setUser(appUser);
      setIsPremium(appUser?.isPremium ?? false);
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    try {
      if(typeof window !== 'undefined') {
          localStorage.removeItem('noor-navigator-premium');
      }
      await signOut(auth);
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      // onAuthStateChanged handles setting user to null and loading to false
    }
  };

  const sendPasswordReset = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err: any)
      {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!auth.currentUser) throw new Error("No user is signed in.");
      await sendEmailVerification(auth.currentUser);
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }

  const reloadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!auth.currentUser) throw new Error("No user is signed in.");
      await auth.currentUser.reload();
      const appUser = buildAppUser(auth.currentUser);
      setUser(appUser);
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }

  const updateUserProfile = async (name: string, photoURL: string | null) => {
    setLoading(true);
    setError(null);
    try {
      if (!auth.currentUser) throw new Error("No user is signed in.");
      
      await updateProfile(auth.currentUser, { 
        displayName: name,
        photoURL: photoURL !== null ? photoURL : auth.currentUser.photoURL
      });

      // also update the user document in firestore
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        displayName: name,
        photoURL: photoURL !== null ? photoURL : auth.currentUser.photoURL
      });
      
      await auth.currentUser.reload();
      const firebaseUser = auth.currentUser;
      const updatedUser = buildAppUser(firebaseUser);
      setUser(updatedUser);
      
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, isOwner, isPremium, isEarlySupporter, signup, login, logout, sendPasswordReset, updateUserProfile, checkPremiumStatus, resendVerificationEmail, reloadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
